import {
	dashboardModelTranslator,
	DashboardProps,
	isChartWidgetProps,
	translateFiltersAndRelationsToDto,
	WidgetDto,
	widgetModelTranslator,
	WidgetProps,
	WidgetsPanelColumnLayout,
	DashboardDto,
	getDashboardModel,
} from '@sisense/sdk-ui';
import { HttpClient } from '@sisense/sdk-rest-client';
import { getDataSourceByTitle } from '@/services/data-sources/data-sources-service';
import { getHttpClient } from '@/singleton-clients';
import { normalizeUrl } from '@/utils/utils';
import {
	convertJaqlDataSourceForDto,
	Filter,
	FilterRelations,
	isDataSourceInfo,
	JaqlDataSourceForDto,
} from '@sisense/sdk-data';

const FUSION_DASHBOARDS_API_URL_PATH = 'api/v1/dashboards';

type GetDashboardsOptions = {
	searchByTitle?: string;
	fields?: string[];
	expand?: string[];
};

export type Dashboard = {
	dashboardId: string;
	dashboardTitle: string;
	dataSourceTitle: string;
};

export function getFusionDashboardUrl(
	dashboardOid: string,
	httpClient?: HttpClient,
): string {
	const client = httpClient ?? getHttpClient();
	return `${normalizeUrl(client.url)}app/main/dashboards/${dashboardOid}`;
}

/**
 * Get all dashboards
 */
export async function getFusionDashboards(
	options: GetDashboardsOptions = {},
	httpClient?: HttpClient,
): Promise<DashboardDto[]> {
	const client = httpClient ?? getHttpClient();
	const { fields, expand, searchByTitle } = options;
	const queryParams = new URLSearchParams({
		...(searchByTitle && { name: searchByTitle }),
		...(fields?.length && { fields: fields?.join(',') }),
		...(expand?.length && { expand: expand?.join(',') }),
	}).toString();

	const dashboards = await client.get<DashboardDto[]>(
		`${FUSION_DASHBOARDS_API_URL_PATH}?${queryParams}`,
	);

	if (!dashboards) return [];

	return dashboards;
}

/**
 * Get all widgets of a specific dashboard
 */
export async function getFusionDashboardWidgets(
	dashboardOid: string,
	httpClient?: HttpClient,
): Promise<WidgetDto[]> {
	const client = httpClient ?? getHttpClient();
	try {
		const widgets = await client.get<WidgetDto[]>(
			`${FUSION_DASHBOARDS_API_URL_PATH}/${dashboardOid}/widgets`,
		);
		return widgets ?? [];
	} catch {
		// when error is encountered, API may return only status code 422 without informative error message
		// to remedy, catch error and throw a more informative error message
		throw new Error(
			'Failed to retrieve dashboard widgets. Please make sure the dashboard exists and is accessible.',
		);
	}
}

/**
 * Get dashboard model from Fusion
 */
export async function getFusionDashboardModel(
	dashboardOid: string,
	httpClient?: HttpClient,
) {
	const client = httpClient ?? getHttpClient();
	return await getDashboardModel(client, dashboardOid, {
		includeWidgets: true,
		includeFilters: true,
	});
}

/**
 * Partially update a dashboard
 */
export async function patchFusionDashboard(
	dashboardOid: string,
	dashboard: Partial<DashboardDto>,
	httpClient?: HttpClient,
): Promise<DashboardDto | null> {
	const client = httpClient ?? getHttpClient();
	const result = await client.patch<DashboardDto>(
		`${FUSION_DASHBOARDS_API_URL_PATH}/${dashboardOid}`,
		dashboard,
	);
	return result ?? null;
}

/**
 * Add widget to a dashboard
 */
export async function addWidgetToFusionDashboard(
	dashboardOid: string,
	widgetDto: WidgetDto,
	httpClient?: HttpClient,
): Promise<WidgetDto | null> {
	const client = httpClient ?? getHttpClient();
	const result = await client.post<WidgetDto>(
		`${FUSION_DASHBOARDS_API_URL_PATH}/${dashboardOid}/widgets`,
		widgetDto,
	);
	return result ?? null;
}

/**
 * Create an empty dashboard
 */
export async function createEmptyFusionDashboard(
	dashboardTitle: string,
	dataSource: JaqlDataSourceForDto,
	httpClient?: HttpClient,
): Promise<DashboardDto | null> {
	const client = httpClient ?? getHttpClient();
	const result = await client.post<DashboardDto>(
		`${FUSION_DASHBOARDS_API_URL_PATH}`,
		{
			title: dashboardTitle,
			datasource: dataSource,
			settings: { managedByTool: 'studio-assistant' },
		},
	);
	return result ?? null;
}

/**
 * Delete a dashboard
 */
export function deleteFusionDashboard(
	dashboardOid: string,
	httpClient?: HttpClient,
): Promise<void> {
	const client = httpClient ?? getHttpClient();
	return client.delete(`${FUSION_DASHBOARDS_API_URL_PATH}/${dashboardOid}`);
}

/**
 * Delete a widget from a dashboard
 */
function deleteWidgetFromFusionDashboard(
	dashboardOid: string,
	widgetOid: string,
	httpClient?: HttpClient,
): Promise<void> {
	const client = httpClient ?? getHttpClient();
	return client.delete(
		`${FUSION_DASHBOARDS_API_URL_PATH}/${dashboardOid}/widgets/${widgetOid}`,
	);
}

/**
 * Translate layout to DTO format
 */
function translateToLayoutDto(layout: WidgetsPanelColumnLayout) {
	return {
		columns: layout.columns.map((c) => ({
			width: c.widthPercentage,
			cells: c.rows.map((r) => ({
				subcells: r.cells.map((cell) => ({
					width: cell.widthPercentage,
					elements: [
						{
							height: cell.height ?? 384,
							widgetid: cell.widgetId,
							widthPercentage: 100,
							minWidth: 200,
							maxWidth: 2000,
							minHeight: 200,
							maxHeight: 2000,
						},
					],
				})),
			})),
		})),
	};
}

/**
 * Get available Fusion dashboards (filtered by studio-assistant)
 */
export async function getAvailableFusionDashboards(
	httpClient?: HttpClient,
): Promise<Dashboard[]> {
	const dashboardDtos = await getFusionDashboards(
		{
			fields: ['title', 'oid', 'datasource', 'settings'],
		},
		httpClient,
	);
	const dashboards: Dashboard[] = dashboardDtos
		// TEMP: For public preview, show only dashboards managed by the studio assistant
		.filter(
			(dashboard) =>
				dashboard.settings?.managedByTool === 'studio-assistant',
		)
		.map((dashboard) => ({
			dashboardTitle: dashboard.title,
			dashboardId: dashboard.oid,
			dataSourceTitle: dashboard.datasource.title,
		}));

	return dashboards;
}

/**
 * Get Fusion dashboard props
 */
export async function getFusionDashboardProps(
	dashboardOid: string,
	httpClient?: HttpClient,
): Promise<DashboardProps | undefined> {
	const dashboardModel = await getFusionDashboardModel(
		dashboardOid,
		httpClient,
	);
	// TEMP: For public preview, show only dashboards created by the studio assistant
	if (
		dashboardModel &&
		dashboardModel.settings?.managedByTool !== 'studio-assistant'
	) {
		return undefined;
	}
	return dashboardModel
		? dashboardModelTranslator.toDashboardProps(dashboardModel)
		: undefined;
}

/**
 * Load existing dashboard from Fusion
 */
export async function loadExistingDashboardFromFusion(
	dashboardId: string,
	httpClient?: HttpClient,
): Promise<DashboardProps> {
	const fusionDashboardProps = await getFusionDashboardProps(
		dashboardId,
		httpClient,
	);
	if (!fusionDashboardProps) {
		throw new Error(
			'Dashboard not available for editing. Please specify a different dashboard.',
		);
	}
	return fusionDashboardProps;
}

/**
 * Add widgets to Fusion dashboard
 */
export async function addWidgetsToFusionDashboard(
	dashboardOid: string,
	widgetsToAdd: WidgetProps[],
	httpClient?: HttpClient,
): Promise<WidgetDto[]> {
	if (widgetsToAdd.length === 0) {
		return [];
	}

	const widgetDtos = widgetsToAdd.map((widgetProps) => {
		const widgetModel = widgetModelTranslator.fromWidgetProps(widgetProps);
		const dataSource =
			isChartWidgetProps(widgetProps) && widgetProps.dataSource
				? convertJaqlDataSourceForDto(widgetProps.dataSource)
				: undefined;
		return widgetModelTranslator.toWidgetDto(widgetModel, dataSource);
	});

	// Create array of promises while preserving order
	const widgetPromises = widgetDtos.map((widgetDto, index) =>
		addWidgetToFusionDashboard(dashboardOid, widgetDto, httpClient).then(
			(result) => ({
				index,
				result,
			}),
		),
	);
	// await is needed so we can align widget OIDs with input widgets later
	const results = await Promise.all(widgetPromises);
	// Sort results by original index to maintain order
	const updatedWidgetDtos = results
		.sort((a, b) => a.index - b.index)
		.map((r) => r.result)
		.filter((dto): dto is WidgetDto => dto !== undefined);

	return updatedWidgetDtos;
}

/**
 * Update Fusion dashboard filters
 */
export async function updateFusionDashboardFilters(
	dashboardOid: string,
	filtersToUpdate: Filter[] | FilterRelations,
	httpClient?: HttpClient,
) {
	const filtersDto = translateFiltersAndRelationsToDto(filtersToUpdate);
	await patchFusionDashboard(dashboardOid, filtersDto, httpClient);
}

/**
 * Check if dashboard is saved
 */
export function isDashboardSaved(dashboardProps: DashboardProps): boolean {
	return !!dashboardProps.id;
}

/**
 * Persist dashboard to Fusion
 */
export async function persistDashboardToFusion(
	dashboardProps: DashboardProps,
	httpClient?: HttpClient,
): Promise<DashboardProps> {
	const dataSource = dashboardProps.defaultDataSource;

	if (!dataSource) {
		throw new Error('Dashboard must have a data source');
	}

	const dataSourceTitle = isDataSourceInfo(dataSource)
		? dataSource.title
		: dataSource;

	const jaqlDataSource = await getDataSourceByTitle(
		dataSourceTitle,
		httpClient,
	);

	if (!jaqlDataSource) {
		throw new Error(
			'Error: Data source not found. Please ask the user to specify a valid data source.',
		);
	}

	let dashboardOid: string;
	let dashboardTitle: string =
		dashboardProps.title || `Dashboard for ${dataSourceTitle}`;

	if (dashboardProps.id) {
		dashboardOid = dashboardProps.id;
		try {
			// Dashboard already exists in Fusion - update it
			await patchFusionDashboard(
				dashboardProps.id,
				{
					title: dashboardTitle,
				},
				httpClient,
			);
			// and delete all existing widgets in parallel to re-populate them later
			const existingWidgetDtos = await getFusionDashboardWidgets(
				dashboardOid,
				httpClient,
			);
			if (existingWidgetDtos) {
				await Promise.all(
					existingWidgetDtos.map((widgetDto) =>
						deleteWidgetFromFusionDashboard(
							dashboardOid,
							widgetDto.oid,
							httpClient,
						),
					),
				);
			}
		} catch (error: any) {
			// If dashboard not found (404), create a new one instead
			if (error?.status === '404' || error?.status === 404) {
				const fusionDashboardDto = await createEmptyFusionDashboard(
					dashboardTitle,
					jaqlDataSource,
					httpClient,
				);
				if (!fusionDashboardDto) {
					throw new Error('Error: Failed to create Fusion dashboard');
				}
				dashboardOid = fusionDashboardDto.oid;
				dashboardTitle = fusionDashboardDto.title;
			} else {
				// Re-throw other errors
				throw error;
			}
		}
	} else {
		// Dashboard doesn't exist in Fusion yet - create it
		const fusionDashboardDto = await createEmptyFusionDashboard(
			dashboardTitle,
			jaqlDataSource,
			httpClient,
		);
		if (!fusionDashboardDto) {
			throw new Error('Error: Failed to create Fusion dashboard');
		}
		dashboardOid = fusionDashboardDto.oid;
		dashboardTitle = fusionDashboardDto.title;
	}

	const updatedDashboardProps = {
		...dashboardProps,
		id: dashboardOid,
		title: dashboardTitle,
	};

	// Add widgets to the Fusion dashboard
	const updatedWidgetDtos = await addWidgetsToFusionDashboard(
		dashboardOid,
		dashboardProps.widgets,
		httpClient,
	);

	// update IDs of updated widget Dtos to input widgets
	const updatedWidgets = dashboardProps.widgets.map((widget, index) => ({
		...widget,
		id: updatedWidgetDtos[index]?.oid ?? widget.id,
		oldId: widget.id,
	}));

	// iterate through columns and cells in dashboardProps.layoutOptions.widgetsPanel
	// and update the widgetId of each cell to the id of the widget in updatedWidgets
	const updatedWidgetsLayout = dashboardProps.layoutOptions?.widgetsPanel
		? {
				...dashboardProps.layoutOptions.widgetsPanel,
				columns: dashboardProps.layoutOptions.widgetsPanel.columns.map(
					(column) => ({
						...column,
						rows: column.rows.map((row) => ({
							...row,
							cells: row.cells.map((cell) => ({
								...cell,
								widgetId:
									updatedWidgets.find(
										(w) => w.oldId === cell.widgetId,
									)?.id || cell.widgetId,
							})),
						})),
					}),
				),
		  }
		: dashboardProps.layoutOptions?.widgetsPanel;

	// Update layout
	if (updatedWidgetsLayout) {
		await patchFusionDashboard(
			dashboardOid,
			{
				layout: translateToLayoutDto(updatedWidgetsLayout),
			},
			httpClient,
		);
	}

	// Update filters
	await updateFusionDashboardFilters(
		dashboardOid,
		dashboardProps.filters ?? [],
		httpClient,
	);

	return {
		...updatedDashboardProps,
		widgets: updatedWidgets,
		layoutOptions: { widgetsPanel: updatedWidgetsLayout },
	};
}
